import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { MakyMascot, MakyMonogram } from "@/components/brand/MakyMark";
import { MenuPicker, type MenuPickerValue } from "@/components/order/MenuPicker";
import { BackLink } from "@/components/layout/BackLink";
import { Button, Chip, Skeleton, Spinner, Tag, TextField } from "@/components/ui";
import { usePersonHistory, usePublicOrder, usePublicPeople, useSubmit } from "@/lib/api/queries";
import type { HistoryChoice } from "@/lib/api/types";

export default function PublicOrderPage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const queryClient = useQueryClient();
  const { data: order, isLoading, isError } = usePublicOrder(slug);
  const { data: people } = usePublicPeople(slug);
  const [personId, setPersonId] = useState<string | null>(null);
  const [picker, setPicker] = useState<MenuPickerValue>({ menuId: null, choiceIds: [], customMenu: "" });
  const [note, setNote] = useState("");
  const [done, setDone] = useState(false);

  const history = usePersonHistory(slug, personId);
  const submit = useSubmit(slug);

  if (isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (isError || !order) {
    return (
      <main className="mx-auto max-w-md p-6 text-center">
        <MakyMascot size={112} className="mx-auto mt-10" />
        <p className="mt-4 text-body-1 text-label-alternative">주문 링크를 찾을 수 없어요.</p>
      </main>
    );
  }

  if (order.status === "closed") {
    const submitted = (people ?? []).filter((p) => p.submitted);
    return (
      <main className="mx-auto max-w-md p-6">
        <h1 className="text-heading-1 font-bold text-label-normal">{order.menuboard.name}</h1>
        <p className="mt-2 text-body-2 text-label-alternative">이미 마감된 주문이에요.</p>
        {submitted.length > 0 && (
          <>
            <h2 className="mt-6 text-label-1 font-semibold text-label-normal">
              {`제출한 사람 ${submitted.length}명`}
            </h2>
            <ul className="mt-2 flex flex-col gap-2">
              {submitted.map((p) => (
                <li key={p.id} className="text-body-2 text-label-normal">
                  {p.name}
                </li>
              ))}
            </ul>
          </>
        )}
      </main>
    );
  }

  const selectedPerson = people?.find((p) => p.id === personId) ?? null;

  if (!personId) {
    return (
      <main className="mx-auto max-w-md p-6">
        <MakyMonogram size={24} />
        <h1 className="mt-2 text-heading-1 font-bold text-label-normal">{order.menuboard.name}</h1>
        <p className="mt-2 text-body-2 text-label-alternative">명단에서 본인 이름을 골라 주세요.</p>
        {people === undefined ? (
          <Skeleton rows={3} height={72} className="mt-4" />
        ) : people.length === 0 ? (
          <p className="mt-4 text-body-2 text-label-alternative">
            명단이 비어 있어요. 링크를 보낸 사람에게 알려 주세요.
          </p>
        ) : (
          // 칸 높이를 고정한다 — "수정" 표시가 붙은 칸만 커지면 줄이 어긋나 보인다.
          <div className="mt-4 grid grid-cols-3 gap-2">
            {people.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setPersonId(p.id)}
                className="flex min-h-[72px] flex-col items-center justify-center gap-1 rounded-md border border-line-normal px-2 py-3"
              >
                <span className="w-full truncate text-center text-body-2 text-label-normal">
                  {p.name}
                </span>
                {p.submitted && (
                  <Tag color="primary" size="small">
                    수정
                  </Tag>
                )}
              </button>
            ))}
          </div>
        )}
      </main>
    );
  }

  // 메뉴를 골랐거나, 자유 입력에 뭔가 적혔을 때만 제출할 수 있다.
  const canSubmit = Boolean(picker.menuId) || picker.customMenu.trim().length > 0;

  function applySuggestion(item: HistoryChoice) {
    setPicker({ menuId: item.menu_id, choiceIds: item.choice_ids, customMenu: "" });
  }

  function backToNamePicker() {
    setPersonId(null);
    setPicker({ menuId: null, choiceIds: [], customMenu: "" });
    setNote("");
  }

  function handleSubmit() {
    if (!personId || !canSubmit) return;
    submit.mutate(
      {
        person_id: personId,
        // 자유 입력이면 menu_id·choice_ids 를 비워 보낸다 — 서버는 둘이 같이 오면 거절한다.
        menu_id: picker.menuId ?? "",
        custom_menu: picker.menuId ? "" : picker.customMenu.trim(),
        choice_ids: picker.menuId ? picker.choiceIds : [],
        note,
      },
      {
        onSuccess: () => setDone(true),
        onError: (error) => {
          const status = axios.isAxiosError(error) ? error.response?.status : undefined;
          if (status === 409) {
            toast.error("방금 마감됐어요");
            queryClient.invalidateQueries({ queryKey: ["public-order", slug] });
            return;
          }
          toast.error("제출하지 못했어요. 다시 시도해 주세요.");
        },
      },
    );
  }

  if (done) {
    return (
      <main className="mx-auto max-w-md p-6 text-center">
        <MakyMascot size={132} className="mx-auto mt-8" />
        <h1 className="mt-4 text-heading-1 font-bold text-label-normal">
          제출 끝! 제가 잘 받아둘게요.
        </h1>
        <p className="mt-2 text-body-2 text-label-alternative">
          {selectedPerson?.name}님, 주문 잘 받았어요.
        </p>
        <div className="mt-6">
          <Button
            variant="outlined"
            onClick={() => {
              setDone(false);
              backToNamePicker();
            }}
          >
            다시 고르기
          </Button>
        </div>
      </main>
    );
  }

  const suggestions = (history.data ?? []).slice(0, 3);

  return (
    <main className="mx-auto max-w-md p-6">
      <BackLink label="이름 다시 고르기" onClick={backToNamePicker} />
      <h1 className="mt-2 text-heading-1 font-bold text-label-normal">{order.menuboard.name}</h1>
      <p className="mt-1 text-body-2 text-label-alternative">
        {selectedPerson?.name}님, 메뉴를 골라 주세요.
      </p>

      {suggestions.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {suggestions.map((item) => (
            <Chip key={`${item.menu_id}-${item.choice_ids.join(",")}`} onClick={() => applySuggestion(item)}>
              {item.option_names.length > 0
                ? `${item.menu_name} (${item.option_names.join(", ")})`
                : item.menu_name}
            </Chip>
          ))}
        </div>
      )}

      <div className="mt-4">
        <MenuPicker menus={order.menuboard.menus} value={picker} onChange={setPicker} />
      </div>

      <div className="mt-4">
        <TextField
          label="요청사항 (선택)"
          placeholder="얼음 적게 주세요"
          maxLength={200}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>

      <div className="mt-4">
        <Button fullWidth onClick={handleSubmit} disabled={!canSubmit || submit.isPending}>
          제출하기
        </Button>
      </div>
    </main>
  );
}
